import { useEffect, useMemo, useState } from 'react';
import { ConstraintConfigType } from 'types/ConstraintsTypes';
import { convertToPercentage, parseValue, reorderMaxMin } from 'utils/misc';
import { GridItem } from './contentComponents/GridItem';
import colorsStore from 'shared/store/colorsStore';
import constraintsStore from 'shared/store/constraintsStore';
import { resolveFieldGroup } from '../../panelSections';

type ConstraintContentProps = {
  configData: ConstraintConfigType;
  dataKey: string;
  notEditableValue?: { value: string; label: string };
  renderCustomContent?: (formValues: Record<string, any>, handleInputChange: any) => JSX.Element;
  disabledKeys?: string[];
};
export function ConstraintContent({ configData, dataKey, notEditableValue, renderCustomContent, disabledKeys }: ConstraintContentProps) {
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);
  const workingDocument = constraintsStore((s) => s.workingDocument);

  const buildFormValues = (): Record<string, string> => {
    return Object.keys(configData).reduce((acc, key) => {
      const group = resolveFieldGroup(dataKey, key);
      const raw = workingDocument?.[group]?.[key];
      let out = '';
      if (typeof raw === 'number') {
        switch (configData[key].type) {
          case 'percentage':
            out = convertToPercentage(raw);
            break;
          case 'float':
            out = raw.toFixed(2);
            break;
          default:
            out = raw.toString();
        }
      }
      acc[key] = out;
      return acc;
    }, {} as Record<string, string>);
  };

  const [formValues, setFormValues] = useState(buildFormValues());

  // Re-derive form values whenever the relevant groups in the working document
  // change — BUT preserve user-typed strings that are still numerically
  // consistent with the store. Without this guard, typing "2.0" into a field
  // that already holds 0.02 would write 0.02 back to the store (no change),
  // skip this effect, and leave the controlled input pinned to the canonical
  // "2.00". And typing "2.1" would commit 0.021 to the store, round-trip
  // through .toFixed(2) to "2.10", and erase the half-typed "2.1" (the user
  // can never reach a single-decimal value because every keystroke that
  // CHANGES the store fires this effect and rewrites the input). Keeping
  // the user's raw string while the numeric meaning matches the store
  // resolves both: external changes (agent, reset, apply) still overwrite
  // because the local string no longer agrees with the derived canonical.
  const relevantGroupsJson = JSON.stringify(
    Array.from(new Set(Object.keys(configData).map((k) => resolveFieldGroup(dataKey, k))))
      .reduce((acc, g) => { acc[g] = workingDocument?.[g] ?? null; return acc; }, {} as Record<string, unknown>),
  );
  useEffect(() => {
    const derived = buildFormValues();
    setFormValues((prev) => {
      const next: Record<string, string> = {};
      let changed = false;
      for (const key of Object.keys(derived)) {
        const localValue = prev[key];
        const derivedValue = derived[key];
        if (typeof localValue === 'string' && localValue !== derivedValue) {
          // Compare numerically — "2.1" and "2.10" mean the same thing, so
          // the user-typed string is kept; "2.5" against "2.10" is a real
          // mismatch and the derived value wins.
          const localNum = parseFloat(localValue.replace(',', '.'));
          const derivedNum = parseFloat(derivedValue);
          if (
            !isNaN(localNum) &&
            !isNaN(derivedNum) &&
            Math.abs(localNum - derivedNum) < 1e-9
          ) {
            next[key] = localValue;
            continue;
          }
        }
        if (localValue !== derivedValue) changed = true;
        next[key] = derivedValue;
      }
      return changed ? next : prev;
    });
  }, [relevantGroupsJson]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fieldName = e.target.id;
    const parsed = parseValue(e.target, configData);
    const group = resolveFieldGroup(dataKey, fieldName);
    let storeValue: unknown = parsed;
    if (typeof parsed === 'string' && parsed !== '') {
      const numVal = parseFloat(parsed);
      if (!isNaN(numVal)) {
        const fieldType = configData[fieldName]?.type;
        if (fieldType === 'percentage') {
          storeValue = numVal / 100;
        } else {
          storeValue = numVal;
        }
      }
    } else if (typeof parsed === 'string' && parsed === '') {
      // Empty input writes `null` (the off-state convention). Predefiniti
      // and saved profiles store unset / cleared fields as `null`; writing
      // empty string `''` would produce a stale `(modificato)` flash after
      // the user clears an input back to its baseline value. Mirrors the
      // VaR-input clear handler in RiskContent.tsx.
      storeValue = null;
    }
    // Keep the user's raw typed string in formValues so intermediate inputs
    // (e.g. "2.0" while editing toward "2.1") don't snap back to the
    // canonical 2-decimal form on the very next render. The re-derive
    // effect above preserves this string while it numerically matches the
    // store, so the input stays responsive to typing.
    setFormValues((prev) => ({ ...prev, [fieldName]: typeof parsed === 'string' ? parsed : '' }));
    constraintsStore.getState().applyEdit(group, fieldName, storeValue);
  };

  // Compute display values: blank out disabled keys so inputs show empty
  const displayFormValues = useMemo(() => {
    if (!disabledKeys || !formValues) return formValues;
    const result = { ...formValues };
    for (const key of disabledKeys) {
      if (key in result) {
        result[key] = '';
      }
    }
    return result;
  }, [formValues, disabledKeys]);

  let groupedKeys = [];
  const keys = Object.keys(configData);
  if (dataKey === 'macro') {
      const reordered = [...keys];

      const eIndex = reordered.indexOf('emerging_equities_min');
      const pIndex = reordered.indexOf('pacific_equities_min');
      if (eIndex !== -1 && pIndex !== -1 && eIndex > pIndex) {
        const ePair = reordered.splice(eIndex, 2);
        const pNewIndex = reordered.indexOf('pacific_equities_min');
        const pPair = reordered.splice(pNewIndex, 2);
        reordered.splice(pNewIndex, 0, ...ePair, ...pPair);
      }

      const sIndex = reordered.indexOf('short_term_min');
      const mIndex = reordered.indexOf('money_market_min');
      if (sIndex !== -1 && mIndex !== -1 && sIndex > mIndex) {
        const sPair = reordered.splice(sIndex, 2);
        const mNewIndex = reordered.indexOf('money_market_min');
        const mPair = reordered.splice(mNewIndex, 2);
        reordered.splice(mNewIndex, 0, ...sPair, ...mPair);
      }

      groupedKeys = reordered.reduce((acc, key, index, arr) => {
        if (index % 2 === 0) acc.push([arr[index], arr[index + 1] || '']);
        return reorderMaxMin(acc);
      }, []);
    } else if (dataKey === 'asset') {
      // Special handling for asset: investment threshold fields are single values (no min/max pair)
      const singleValueKeys = ['investment_bonds_max', 'investment_funds_max'];
      const regularKeys = Object.keys(configData).filter((key) => !singleValueKeys.includes(key));
      const singleKeys = Object.keys(configData).filter((key) => singleValueKeys.includes(key));

      // Group regular keys in pairs (min/max)
      groupedKeys = regularKeys.reduce((acc, key, index, arr) => {
        if (index % 2 === 0) acc.push([arr[index], arr[index + 1] || '']);
        return reorderMaxMin(acc);
      }, []);

      // Add single value keys as standalone rows
      singleKeys.forEach((key) => {
        groupedKeys.push([key, '']);
      });
    } else if (dataKey === 'order') {
      // Special handling for order: positive_amount_min is a single value (no max counterpart)
      // and should appear after positive_value fields
      const singleValueKeys = ['positive_amount_min'];
      const regularKeys = Object.keys(configData).filter((key) => !singleValueKeys.includes(key));

      // Group regular keys in pairs (min/max)
      const regularGroupedKeys = regularKeys.reduce((acc, key, index, arr) => {
        if (index % 2 === 0) acc.push([arr[index], arr[index + 1] || '']);
        return reorderMaxMin(acc);
      }, []);

      // Insert positive_amount_min after the first pair (positive_value_min/max)
      groupedKeys = [];
      regularGroupedKeys.forEach((pair, index) => {
        groupedKeys.push(pair);
        // Insert single value after the first pair (positive_value)
        if (index === 0) {
          singleValueKeys.forEach((key) => {
            if (configData[key]) {
              groupedKeys.push([key, '']);
            }
          });
        }
      });
  } else {
    // Default pairing (cost, dividend, top_quality, currency_exposure, indicator, ...):
    // pair _min/_max into a single grid item. For sections whose gridType only
    // renders cv1 (e.g. indicator → gridType 2), this still produces the right
    // visual — one row per indicator pair, Min input only.
    groupedKeys = Object.keys(configData).reduce((acc, key, index, arr) => {
      if (index % 2 === 0) acc.push([arr[index], arr[index + 1] || '']);
      return reorderMaxMin(acc);
    }, []);
  }

  return (
    <div style={{ marginTop: 0, height: 'auto' }}>
      {renderCustomContent ? (
        renderCustomContent(formValues, handleInputChange)
      ) : (
        <div
          // className={mainStyles.gridContainer1x4}
          style={{
            color: colorsConfig.finecoBlack,
            fontSize: 14,
            marginTop: 0,
          }}
        >
          {groupedKeys.map(([key1, key2], index) => {
            const isDisabled = disabledKeys
              ? disabledKeys.includes(key1) || (key2 && disabledKeys.includes(key2))
              : false;
            return (
              <GridItem
                key={`grid-item-${index}`}
                dataKey={dataKey}
                cd={configData}
                cv1={key1}
                cv2={key2}
                fv={isDisabled ? displayFormValues : formValues}
                ohi={handleInputChange}
                index={index}
                notEditableValue={notEditableValue}
                disabled={isDisabled}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
